import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Mail, Target } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

/** Productive-hours targets (default / per team / per person) and the
 *  "below target N days running" alert that emails managers. */
export function ProdGoalsCard({ canManage }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [recip, setRecip] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/productivity/goals").then(({ data: d }) => {
    setData(d);
    setCfg({ ...d.config });
    setRecip((d.config.recipients || []).join(", "));
  }).catch((e) => toast.error(e.response?.data?.detail || "Could not load targets"));

  useEffect(() => { load(); }, []);

  const patch = (p) => setCfg((c) => ({ ...c, ...p }));

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/productivity/goals", {
        default_hours: Number(cfg.default_hours),
        threshold_pct: Number(cfg.threshold_pct),
        consecutive_days: Number(cfg.consecutive_days),
        alerts_enabled: !!cfg.alerts_enabled,
        workdays: cfg.workdays,
        teams: cfg.teams || {},
        people: cfg.people || {},
        recipients: recip,
      });
      setCfg({ ...d.config });
      setRecip((d.config.recipients || []).join(", "));
      toast.success("Targets saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/productivity/alerts/send-now");
      if (r.status === "nothing_to_flag") toast.success("Scan complete — nobody is below target");
      else if (r.status === "sent") toast.success(`Alert sent — ${r.flagged} flagged`);
      else if (r.status === "paused") toast.warning("Outbound email is paused — nothing delivered");
      else toast.error(`Alert status: ${r.status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return <Card className="p-4"><Loader2 className="h-4 w-4 animate-spin text-slate-300 dark:text-slate-500" /></Card>;

  return (
    <Card className="p-4" data-testid="prod-settings-goals">
      <div className="mb-3 flex items-center gap-2">
        <Target className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Targets &amp; alerts</h3>
      </div>
      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        Set the productive-hours target for a day. Anyone who sits below the threshold for the
        set number of working days in a row is emailed to managers each weekday morning.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          Default target (hours/day)
          <Input type="number" step="0.25" min="0.5" max="16" value={cfg.default_hours}
            disabled={!canManage} data-testid="prod-goal-default"
            onChange={(e) => patch({ default_hours: e.target.value })} className="mt-1 h-8" />
        </label>
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          Flag below (% of target)
          <Input type="number" min="1" max="100" value={cfg.threshold_pct}
            disabled={!canManage} data-testid="prod-goal-threshold"
            onChange={(e) => patch({ threshold_pct: e.target.value })} className="mt-1 h-8" />
        </label>
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          For this many working days
          <Input type="number" min="1" max="30" value={cfg.consecutive_days}
            disabled={!canManage} data-testid="prod-goal-consecutive"
            onChange={(e) => patch({ consecutive_days: e.target.value })} className="mt-1 h-8" />
        </label>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">Working days</div>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map(([label, n]) => {
            const on = (cfg.workdays || []).includes(n);
            return (
              <button key={n} type="button" disabled={!canManage}
                data-testid={`prod-goal-workday-${n}`}
                onClick={() => patch({
                  workdays: on ? cfg.workdays.filter((d) => d !== n)
                    : [...(cfg.workdays || []), n].sort(),
                })}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  on ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200"}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">Per-team target override</div>
          <div className="space-y-1.5">
            {(data?.teams || []).map((t) => (
              <div key={t} className="flex items-center gap-2">
                <span className="w-32 text-xs text-slate-600 dark:text-slate-300">{t}</span>
                <Input type="number" step="0.25" min="0.5" max="16" className="h-7 w-24 text-xs"
                  disabled={!canManage} data-testid={`prod-goal-team-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  placeholder={String(cfg.default_hours)} value={(cfg.teams || {})[t] ?? ""}
                  onChange={(e) => patch({
                    teams: { ...(cfg.teams || {}), [t]: e.target.value === "" ? undefined : e.target.value },
                  })} />
                <span className="text-xs text-slate-400 dark:text-slate-400">h/day</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">Per-person target override</div>
          <div className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
            {(data?.roster || []).map((p) => (
              <div key={p.key} className="flex items-center gap-2">
                <span className="w-32 truncate text-xs text-slate-600 dark:text-slate-300" title={p.name}>{p.name}</span>
                <Input type="number" step="0.25" min="0.5" max="16" className="h-7 w-24 text-xs"
                  disabled={!canManage} data-testid={`prod-goal-person-${p.key}`}
                  placeholder={String(p.goal_hours)} value={(cfg.people || {})[p.key] ?? ""}
                  onChange={(e) => patch({
                    people: { ...(cfg.people || {}), [p.key]: e.target.value === "" ? undefined : e.target.value },
                  })} />
                <span className="text-xs text-slate-400 dark:text-slate-400">h/day</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Email the alert to managers</span>
          <Switch checked={!!cfg.alerts_enabled} disabled={!canManage} data-testid="prod-goal-alerts-enabled"
            onCheckedChange={(v) => patch({ alerts_enabled: v })} />
        </div>
        <label className="mt-2 block text-xs text-slate-600 dark:text-slate-300">
          Recipients
          <Textarea value={recip} onChange={(e) => setRecip(e.target.value)} rows={2}
            disabled={!canManage} data-testid="prod-goal-recipients"
            placeholder="reo@unitedcloud.ca" className="mt-1 text-sm" />
        </label>
        <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          Tracked staff can&apos;t be added here — reports and alerts go to managers only.
        </p>
        {data?.last_send && (
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="prod-goal-last-send">
            Last scan: {data.last_send.sent_at?.slice(0, 16).replace("T", " ")} UTC ·{" "}
            {data.last_send.status} · {data.last_send.flagged || 0} flagged
          </div>
        )}
        {canManage && (
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="outline" onClick={sendNow} disabled={busy} data-testid="prod-goal-send-now">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
              Run scan now
            </Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="prod-goal-save">Save targets</Button>
          </div>
        )}
      </div>
    </Card>
  );
}
